import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, LockOpen } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { getErrorMessage } from "@/lib/errors";

import { fmtDate } from "@/lib/format";
export default function PeriodsYearEnd() {
  const { isOrgAdmin } = useAuth();
  const { selectedCompanyId } = useCompany() || {};
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!selectedCompanyId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/gl/periods?company_id=${selectedCompanyId}`);
      setPeriods(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [selectedCompanyId]);

  const toggle = async (period) => {
    try {
      const action = period.status === "OPEN" ? "close" : "reopen";
      await api.post(`/gl/periods/${period.id}/${action}`);
      toast.success(`Period ${period.period_key} ${action === "close" ? "closed" : "reopened"}`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="periods-year-end-page">
      <PageHeader
        title="Periods & Year-End"
        description="Monthly fiscal periods for the selected company. Closing a period blocks any further postings."
      />
      {!selectedCompanyId ? (
        <EmptyState title="Select a company from the top bar first" testId="periods-no-company" />
      ) : !loading && periods.length === 0 ? (
        <EmptyState title="No periods yet" testId="periods-empty-state" />
      ) : (
        <div className="rounded-xl border border-border bg-card">
          <Table>
            <TableHeader><TableRow><TableHead>Period</TableHead><TableHead>From</TableHead><TableHead>To</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {periods.map((p) => (
                <TableRow key={p.id} data-testid={`period-row-${p.period_key}`}>
                  <TableCell className="font-mono text-xs text-foreground">{p.period_key}</TableCell>
                  <TableCell className="text-muted-foreground">{fmtDate(p.start_date)}</TableCell>
                  <TableCell className="text-muted-foreground">{fmtDate(p.end_date)}</TableCell>
                  <TableCell><Badge variant={p.status === "OPEN" ? "default" : "secondary"}>{p.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    {isOrgAdmin && (
                      <Button size="sm" variant="outline" onClick={() => toggle(p)} data-testid={`toggle-period-button-${p.period_key}`}>
                        {p.status === "OPEN" ? <><Lock className="h-3.5 w-3.5" /> Close</> : <><LockOpen className="h-3.5 w-3.5" /> Reopen</>}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
